// Help's table of contents: the Mac's `HelpSection.all` (Apps/Shared/HelpView.swift), by article id (a file name in
// Apps/Shared/Help without `.md`). The articles themselves are bundled from that folder when the app is built
// (scripts/build.mjs), never copied into Web. An article the list doesn't name is shown under "More".
export const HELP_SECTIONS: readonly { name: string; ids: readonly string[] }[] = [
  {
    name: 'Writing',
    ids: ['rooms', 'sample-project', 'scrivenings', 'prose-formatting', 'binder', 'filters', 'corkboard', 'outliner', 'fields', 'beats', 'mind-maps', 'templates', 'research', 'collections', 'tags', 'composition-mode', 'screenplays', 'journals', 'iphone'],
  },
  { name: 'Story', ids: ['mentions', 'character-studies', 'name-generator', 'glossary', 'story-time', 'story-view', 'shape', 'continuity'] },
  { name: 'Revising', ids: ['revision-mode', 'find-replace', 'read-aloud', 'margin-comments', 'checkpoints', 'alternates', 'style', 'cleanup', 'statistics', 'sessions', 'heatmap'] },
  { name: 'Feedback', ids: ['beta-readers', 'share-for-review', 'word-review', 'pencil-markup'] },
  { name: 'Publishing', ids: ['compile', 'print-interiors', 'book-matter', 'retail-details', 'audiobooks', 'workbench'] },
  { name: 'Your Work, Everywhere', ids: ['importing', 'sync', 'web', 'sharing', 'backups', 'mirror', 'assistants', 'system'] },
];

export interface HelpArticle {
  id: string;
  title: string;
  markdown: string;
}

/** An article's title: its first line's `# …`, else its id (`HelpTopic.load`). */
export function articleTitle(id: string, markdown: string): string {
  const first = markdown.split('\n', 1)[0] ?? '';
  return first.startsWith('# ') ? first.slice(2).trim() : id;
}

/** The sections with the articles there are (in the Mac's order), then any the list doesn't name. */
export function sectionsOf(articles: Record<string, string>): { name: string; topics: HelpArticle[] }[] {
  const named = new Set(HELP_SECTIONS.flatMap((s) => s.ids));
  const topic = (id: string): HelpArticle => ({ id, title: articleTitle(id, articles[id]), markdown: articles[id] });
  const out = HELP_SECTIONS.map((s) => ({ name: s.name, topics: s.ids.filter((id) => id in articles).map(topic) })).filter((s) => s.topics.length);
  const more = Object.keys(articles)
    .filter((id) => !named.has(id))
    .sort()
    .map(topic);
  return more.length ? [...out, { name: 'More', topics: more }] : out;
}

/** Topics whose text contains every word of the query (`HelpSection.matching`). */
export function matching(sections: { name: string; topics: HelpArticle[] }[], query: string) {
  const words = query.toLowerCase().split(' ').filter(Boolean);
  if (!words.length) return sections;
  return sections
    .map((s) => ({ name: s.name, topics: s.topics.filter((t) => words.every((w) => t.markdown.toLowerCase().includes(w))) }))
    .filter((s) => s.topics.length);
}
